import React, { useEffect, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Trash2,
  Search,
  Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";

export default function HistoryPage() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    load();
    const i = setInterval(load, 3000);
    return () => clearInterval(i);
  }, []);

  const load = async () => {
    const { data } = await api.get("/campaigns");
    setItems(data);
  };

  const openDetail = async (id) => {
    if (expanded === id) {
      setExpanded(null);
      setDetail(null);
      return;
    }
    setExpanded(id);
    const { data } = await api.get(`/campaigns/${id}`);
    setDetail(data);
  };

  const deleteCampaign = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Excluir esta campanha?")) return;
    await api.delete(`/campaigns/${id}`);
    toast.success("Campanha excluída");
    setExpanded(null);
    load();
  };

  const filtered = items.filter((c) => !q || c.title.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 md:p-8 max-w-[1400px]">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="font-mono-label text-zinc-500 mb-1">Registros</p>
          <h1 className="font-display text-3xl font-bold tracking-tight">Histórico de Divulgações</h1>
        </div>
        <div className="relative w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            placeholder="Buscar pelo texto da publicação..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="history-search-input"
            className="pl-10 rounded-sm h-10"
          />
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="grid grid-cols-[auto_1fr_140px_120px_120px_140px_40px] gap-4 px-5 py-3 border-b border-zinc-100 font-mono-label text-zinc-500 bg-zinc-50">
          <span className="w-6" />
          <span>Publicação</span>
          <span>Status</span>
          <span>Enviados</span>
          <span>Falhas</span>
          <span>Criado em</span>
          <span />
        </div>
        {filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-zinc-500">Nenhuma campanha ainda. Vá para o Painel e crie sua primeira divulgação.</div>
        ) : (
          filtered.map((c) => (
            <div key={c.id} className="border-b border-zinc-100 last:border-0">
              <div
                onClick={() => openDetail(c.id)}
                className="grid grid-cols-[auto_1fr_140px_120px_120px_140px_40px] gap-4 px-5 py-4 items-center hover:bg-zinc-50 cursor-pointer"
                data-testid={`history-row-${c.id}`}
              >
                <span className="w-6 flex items-center justify-center text-zinc-400">
                  {expanded === c.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </span>
                <div className="flex items-center gap-3 min-w-0">
                  {c.image_url ? (
                    <img src={mediaUrl(c.image_url)} className="h-10 w-10 rounded-sm object-cover flex-shrink-0" alt="" />
                  ) : (
                    <div className="h-10 w-10 rounded-sm bg-zinc-100 flex items-center justify-center flex-shrink-0">
                      <ImageIcon className="h-4 w-4 text-zinc-400" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{c.title.split("\n")[0]}</div>
                    <div className="text-xs text-zinc-500 font-mono mt-0.5">{c.total} destinos · delay {c.delay_seconds}s · {c.threads} thread(s)</div>
                  </div>
                </div>
                <StatusBadge status={c.status} />
                <span className="font-mono text-sm text-green-600 font-medium">{c.sent}</span>
                <span className="font-mono text-sm text-red-600 font-medium">{c.failed}</span>
                <span className="text-xs text-zinc-500 font-mono">{formatDate(c.created_at)}</span>
                <button
                  onClick={(e) => deleteCampaign(c.id, e)}
                  className="h-7 w-7 flex items-center justify-center text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-sm"
                  data-testid={`history-delete-${c.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>

              {expanded === c.id && detail && (
                <div className="bg-zinc-50 border-t border-zinc-100 p-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5">
                    <div>
                      <p className="font-mono-label text-zinc-500 mb-2">Conteúdo</p>
                      <div className="text-sm whitespace-pre-wrap bg-white border border-zinc-200 rounded-sm p-3">{detail.campaign.title}</div>
                    </div>
                    {detail.campaign.image_url && (
                      <div>
                        <p className="font-mono-label text-zinc-500 mb-2">Imagem</p>
                        <img src={mediaUrl(detail.campaign.image_url)} className="max-h-48 rounded-sm border border-zinc-200" alt="" />
                      </div>
                    )}
                  </div>
                  <p className="font-mono-label text-zinc-500 mb-2">Disparos ({detail.items.length})</p>
                  <div className="bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-100 max-h-96 overflow-y-auto scroll-thin">
                    {detail.items.map((it) => (
                      <div key={it.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                        <ItemStatusIcon status={it.status} />
                        <div className="flex-1 min-w-0">
                          <div className="truncate font-medium">{it.group_name}</div>
                          {it.error && <div className="text-xs text-red-600 mt-0.5">{it.error}</div>}
                        </div>
                        <span className="text-xs text-zinc-400 font-mono">
                          {it.sent_at ? formatDate(it.sent_at, true) : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    queued: { label: "Na fila", cls: "border-zinc-300 text-zinc-700 bg-zinc-50" },
    running: { label: "Executando", cls: "border-blue-300 text-blue-700 bg-blue-50 animate-status-pulse" },
    completed: { label: "Concluído", cls: "border-green-300 text-green-700 bg-green-50" },
    stopped: { label: "Parado", cls: "border-amber-300 text-amber-700 bg-amber-50" },
    failed: { label: "Falhou", cls: "border-red-300 text-red-700 bg-red-50" },
    paused: { label: "Pausado", cls: "border-zinc-300 text-zinc-700 bg-zinc-50" },
  };
  const s = map[status] || map.queued;
  return <Badge variant="outline" className={`rounded-sm font-mono-label ${s.cls}`}>{s.label}</Badge>;
}

function ItemStatusIcon({ status }) {
  if (status === "sent") return <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />;
  if (status === "failed") return <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />;
  if (status === "sending") return <Loader2 className="h-4 w-4 text-blue-500 animate-spin flex-shrink-0" />;
  return <Clock className="h-4 w-4 text-zinc-400 flex-shrink-0" />;
}

function formatDate(v, withSecs) {
  if (!v) return "";
  const d = typeof v === "string" ? new Date(v) : v;
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: withSecs ? "2-digit" : undefined,
  });
}
